import React, { Fragment, Suspense, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import type { LogArgument } from 'rollbar';
import { Link, useLocation } from '@bbkShared/react-router';
import { cs } from '@bbkAdminUtils/css-utils';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { useUrl } from '@bbkAdminUtils/nav';
import { ProfileImage } from '@bbkAdminComponents/profile/profile-image';
import { CompanyLogo } from '@bbkAdminComponents/profile/company-logo';
import LinkListPopover from '@bbkAdminComponents/popovers/link-list-popover';
import type LinkGroupMenu from '@bbkAdminComponents/linkGroup/link-group';
import {
  selectApp,
  selectAppId,
  selectCompany,
  selectCompanyInternalName,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import {
  useGetProductLicense,
  useHasSubscription,
  usePlans,
} from '@bbkAdminUtils/plans';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useSelectCompanySubscription } from '@bbkAdminRedux/rtkq/checkout.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { BottomAlertTab, NavItem } from '@bbkAdminComponents/app-layout/nav';
import {
  CBGrowthLogoSVG,
  CBRetentionLogo,
} from '@bbkAdminComponents/cbretention-logo';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { selectCompanyMisc } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { selectNotPersistedMisc } from '@bbkAdminRedux/rtkq/app-not-persisted.slice';
import { determineLeftNavAppearance } from '@bbkAdminRedux/app/reducers';
import { recordError } from '@bbkAdminUtils/recordError';
import { useRoles } from '@bbkAdminUtils/roles';
import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';
import { CBProductsSwitcher } from '@bbkAdminComponents/CBProductsSwitcher';
import { brightbackAuthSlice } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import icons from '@bbkAdminUtils/icons';
import { invitationSlice } from '@bbkAdminRedux/rtkq/invitation.slice';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { plural } from '@bbkShared/utils/pluralize';
import { BbkNavbar } from '@bbkAdminComponents/app-layout/nav/bbk-navbar';
import { useNavbarConfig } from '@bbkAdminComponents/app-layout/navbar-config';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { NavBarHelper } from '@bbkAdminComponents/app-layout/nav/NavBarHelper';

const WelcomePopup = React.lazy(
  () => import('@bbkAdminComponents/get-started/welcome-popup')
);
const ProductTourModal = React.lazy(
  () => import('@bbkAdminComponents/product-tour/product-tour-modal')
);

type LinkGroupMenuItem = React.ComponentProps<
  typeof LinkGroupMenu
>['links'][number];
export const LeftColumn: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const app = useSelector(selectApp);
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const showNav = determineLeftNavAppearance(location.pathname);
  const createUrl = useUrl();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();
  const { data: pendingInvites = [] } =
    invitationSlice.endpoints.getPendingInvitationsByEmail.useQuery();
  const { showProductTour } = useSelector(selectNotPersistedMisc);

  const showWhatsNew = useFeatureFlags(FeatureFlagsE.SHOW_WHATS_NEW);

  const userAnchorRef = useRef<HTMLDivElement>(null);
  const appAnchorRef = useRef<HTMLDivElement>(null);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const {
    agsNavbarOptions,
    legacyNavbarOptions,
    internalNavbarOptions,
    helpNavbarOptions,
  } = useNavbarConfig();

  const { isSuccess: isFFsSuccess } = useQueryFeatureFlags();

  const appKey = useSelector(selectAppId);
  const companyInternalName = useSelector(selectCompanyInternalName);
  const hasSubscription = useHasSubscription();
  const subscription = useSelectCompanySubscription(
    hasSubscription
      ? {
          company: companyInternalName,
        }
      : skipToken
  );
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const hasChargebeeIntegration =
    billingIntegration?.data_type === IntegrationDataType.CHARGEBEE;

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const deflectLicense = productLicense.deflectLicense;

  const showWizardPopupFF = useFeatureFlags(FeatureFlagsE.SHOW_WELCOME_POPUP);

  const { showWelcomePopup } = useSelector(
    selectCompanyMisc(companyInternalName)
  );

  // Plan filters
  const { isTrial, isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  // show welcome popup once per user for trial/starter
  const isStarter = isStarterFreePlan || isStarterPremiumPlan;
  const showWizardPopupEligible = isTrial || showWizardPopupFF || isStarter;
  const showWizardPopup =
    appKey !== '_' && showWizardPopupEligible && showWelcomePopup;

  const { hasBillingRole } = useRoles();
  const showAlert = useMemo(() => {
    if (!subscription?.price.included_session_limit) return false;
    return (
      hasSubscription &&
      hasBillingRole &&
      subscription?.usage > subscription?.price.included_session_limit
    );
  }, [hasSubscription, subscription, hasBillingRole]);

  if (!user && !company) return null;

  const appCount = user.companies.reduce(
    (ret, c) => ret + c.applications.length,
    0
  );
  const enableAppSwitcher = user.companies.length > 1 || appCount > 1;

  const appMenuGroupItems = NavBarHelper.buildAppsList(user.companies);

  const userMenuGroupItems: (LinkGroupMenuItem | undefined)[] = [
    showWhatsNew
      ? {
          icon: 'whatsNew',
          text: "What's new?",
          linkAction: (): void => {},
          classes: 'logout',
          iconClasses: 'tw-w-3.5',
        }
      : undefined,
    user.user_profile_url
      ? ({
          icon: 'profile',
          text: 'Profile',
          link: user.user_profile_url,
        } satisfies LinkGroupMenuItem)
      : undefined,
    pendingInvites.length
      ? ({
          icon: 'email',
          text: `Pending invites (${pendingInvites.length})`,
          link: LogInPaths.selector,
          iconClasses: '[&_svg]:tw-w-3.5 [&_svg]:tw-h-3.5 tw-pl-0.5',
        } satisfies LinkGroupMenuItem)
      : undefined,
    {
      icon: 'lock',
      text: 'Sign out',
      linkAction: (): void => {
        const { pathname, search, hash } = window.location;
        signOut$({
          redirect: pathname + search + hash,
        })
          .unwrap()
          .catch((err: LogArgument) => {
            recordError(err);
          });
      },
      classes: 'logout',
      iconClasses: 'tw-w-3.5',
    },
  ];

  const appSwitcherSection = enableAppSwitcher && company && (
    <>
      <div ref={appAnchorRef}>
        <NavItem
          classes="app-menu"
          onClick={() => setAppMenuOpen(true)}
          iconNode={
            <CompanyLogo
              className="tw-ml-0.5 tw-w-5 tw-rounded-full"
              company={company}
            />
          }
          text={
            <div className="nav-content">
              {appCount > 1 && (
                <div className="company-name">{company.name}</div>
              )}
              <div className="env" title={company.name}>
                {app?.display_name ?? 'Default'}
              </div>
            </div>
          }
        />
      </div>
      <LinkListPopover
        open={appMenuOpen}
        linkGroupItems={appMenuGroupItems}
        handleClose={() => setAppMenuOpen(false)}
        anchorEl={appAnchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 10,
        }}
        enableSearch
      />
    </>
  );

  const userProfileSection = (
    <>
      <div ref={userAnchorRef}>
        <NavItem
          onClick={() => setUserMenuOpen(true)}
          iconNode={<ProfileImage user={user} />}
          text={
            <>
              {user.details.display_name}
              {pendingInvites.length ? (
                <TippyTooltip
                  content={
                    <>
                      You have {pendingInvites.length} pending
                      {plural('invite', pendingInvites.length)}.
                    </>
                  }
                >
                  <span className="tw-ml-1 tw-rotate-12">{icons.bell}</span>
                </TippyTooltip>
              ) : null}
            </>
          }
        />
      </div>
      <LinkListPopover
        open={userMenuOpen}
        linkGroupItems={userMenuGroupItems.filter(isDefined)}
        handleClose={() => setUserMenuOpen(false)}
        anchorEl={userAnchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 10,
        }}
      />
    </>
  );

  const footerSection = (
    <>
      {hasChargebeeIntegration && <CBProductsSwitcher />}
      <BbkNavbar opts={helpNavbarOptions} />
      {userProfileSection}
      {appSwitcherSection}
      {showAlert && <BottomAlertTab />}
      {showProductTour && (
        <Suspense fallback={<SuspenseLoader message=" " />}>
          <ProductTourModal />
        </Suspense>
      )}
      {deflectLicense && showWizardPopup && (
        <Suspense fallback={<SuspenseLoader message=" " />}>
          <WelcomePopup />
        </Suspense>
      )}
    </>
  );

  const horizontalLine = (
    <div className="-tw-ml-4 tw-mb-6 tw-w-[calc(100%+2rem)] tw-border tw-border-solid tw-border-cbrneutral-200" />
  );
  return (
    <div
      className={cs(
        'tw-flex tw-shrink-0 tw-grow-0 tw-flex-col tw-justify-between',
        'tw-overflow-y-auto tw-px-4 tw-py-6',
        '[&_.ReactCollapse--content]:tw-mb-2.5 [&_.ReactCollapse--content]:tw-ml-8',
        {
          'tw-hidden': !showNav || !isFFsSuccess,
        },
        {
          'tw-basis-[18rem] tw-rounded-r-md tw-bg-white tw-shadow-xl':
            agsLicense,
          'tw-basis-[250px] [&_.bbk-nav-item-expander]:tw-hidden': !agsLicense,
        }
      )}
    >
      <div>
        <Link
          className="tw-mb-6 tw-block tw-pl-2.5"
          to={{
            pathname:
              agsLicense && !deflectLicense
                ? createUrl(AuthorizedPaths.plays)
                : createUrl(AuthorizedPaths.dashboard),
          }}
        >
          {agsLicense ? (
            <span className="[&_svg]:tw-h-11 [&_svg]:tw-w-44">
              {CBGrowthLogoSVG}
            </span>
          ) : (
            <CBRetentionLogo />
          )}
        </Link>
        {agsLicense && horizontalLine}
        <BbkNavbar opts={agsLicense ? agsNavbarOptions : legacyNavbarOptions} />
      </div>
      <div>
        {agsLicense && horizontalLine}
        <BbkNavbar opts={internalNavbarOptions} />
        {footerSection}
      </div>
    </div>
  );
};
